import React, { MutableRefObject, useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import { TooltipArrow, TooltipCall, TooltipText, TooltipWrapper } from './styles'
import { getMeasurementElement } from './utils'

export type TPosition = 'top' | 'left' | 'right' | 'bottom'
export interface ITooltip {
  children: React.ReactNode
  description: string | JSX.Element
  position?: TPosition
  custom?: boolean
  background?: string
  opacity?: number
  isVisible?: boolean
  textMaxWidth?: string
  delay?: number
  hasArrow?: boolean
  margin?: string
  cursor?: string
  positionBasedOn?: MutableRefObject<null | HTMLElement>
  fullWidth?: boolean
}

const Tooltip: React.FC<ITooltip> = ({
  children,
  description,
  position,
  background,
  custom,
  opacity,
  isVisible,
  textMaxWidth,
  delay = 0.3,
  hasArrow,
  margin,
  cursor,
  fullWidth
}) => {
  const [show, setShow] = useState<boolean>(false)
  const wrapperElement: MutableRefObject<null | HTMLElement> = useRef(null)
  const contentElement: MutableRefObject<null | HTMLElement> = useRef(null)

  const TooltipContent = () => {
    const descriptionElementSizes = getMeasurementElement(description)
    const rootHeight = document.body.scrollHeight
    const rootWidth = document.body.scrollWidth

    const elementX = wrapperElement.current?.getBoundingClientRect()?.x ?? 0
    const elementY = wrapperElement.current?.getBoundingClientRect()?.y ?? 0
    const elementWidth = wrapperElement.current?.getBoundingClientRect()?.width ?? 0

    const wrapperHeight = wrapperElement.current?.offsetHeight ?? 0
    const tooltipWrapperExceedsX = elementX + descriptionElementSizes.width > rootWidth
    const tooltipWrapperExceedsY = elementY + wrapperHeight + descriptionElementSizes.height > rootHeight
    const calculateXPosition = tooltipWrapperExceedsX
      ? elementX - descriptionElementSizes.width + elementWidth
      : elementX

    return (
      <TooltipWrapper
        ref={contentElement}
        className="tooltipWrapper"
        custom={custom}
        background={background}
        opacity={opacity}
        show={show}
        isVisible={isVisible}
        position={position ?? tooltipWrapperExceedsY ? 'top' : 'bottom'}
        positionX={calculateXPosition}
        positionY={Number(elementY) + window.scrollY}
        delay={delay}
        hasArrow={hasArrow}
        margin={margin}
        fullWidth={fullWidth}
      >
        <TooltipText textMaxWidth={textMaxWidth}>{custom ? children : description}</TooltipText>
        {hasArrow && <TooltipArrow position={position} background={background} data-testid="tooltip-arrow" />}
      </TooltipWrapper>
    )
  }

  const showTooltipText = () => {
    return ReactDOM.createPortal(<TooltipContent />, document.body)
  }

  return (
    <TooltipCall
      data-testid="tooltip-call"
      ref={wrapperElement}
      onMouseOver={() => setShow(true)}
      onMouseOut={() => setShow(false)}
      cursor={cursor}
    >
      {custom ? description : children}
      {show && showTooltipText()}
    </TooltipCall>
  )
}

export { Tooltip }
